import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { api, thumbUrl, posterUrl, videoUrl, displayTitle } from "@/lib/api";
import { ownerLocationLabel, copyText, xPostProblems } from "@/lib/owner";
import { toast } from "sonner";
import { Check, Copy, Loader2, MapPin, Pencil, RefreshCw, AlertTriangle, CloudOff, Video } from "lucide-react";
import { OwnerChat } from "@/components/OwnerChat";
import { cn } from "@/lib/utils";

const POLL_MS = 3000;

// Honest job chip: processing | succeeded | failed | blocked (media storage) — derived ONLY from the persisted `reprocess` state.
const jobChip = (r) => {
  const s = r?.status;
  if (s === "processing") return { key: "processing", label: `Processing${r.stage && r.stage !== "queued" ? ` · ${r.stage}` : "…"}` };
  if (s === "succeeded") return { key: "succeeded", label: "Succeeded" };
  if (s === "blocked" && /quality/.test(r.caption?.status || "")) return { key: "blocked", label: `Blocked · ${r.caption.status}` };
  if (s === "blocked" && (["r2","bf"].includes(r.video?.backend) || r.video?.reason === "media_store_unavailable")) return { key: "blocked", label: "Blocked · Media storage unavailable" };
  if (s === "blocked") return { key: "blocked", label: "Blocked · media storage unavailable" };
  if (s === "failed") return { key: "failed", label: `Failed · ${r.error || "see details"}` };
  return null;
};
const CHIP_CLASS = { processing: "text-amber-200", succeeded: "text-emerald-300", failed: "text-red-300", blocked: "text-sky-300" };

export const OwnerMediaCard = ({ media, onEdit, onChanged }) => {
  const [m, setM] = useState(media);
  const [job, setJob] = useState(media?.reprocess || null);
  const [cloud, setCloud] = useState(null);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setM(media); setJob(media?.reprocess || null); }, [media]);
  // Video: ask once whether bf playback can be derived right now (media-store health) — never show a player that will 401.
  useEffect(() => {
    if (media?.media_type !== "video" || !media?.id) { setCloud(null); return; }
    let alive = true;
    api.get(`/owner/media/${media.id}/reprocess-status`).then(({ data }) => { if (alive) setCloud(data.media_store || data.cloudinary); }).catch(() => {});
    return () => { alive = false; };
  }, [media?.id, media?.media_type]);
  useEffect(() => { if (!copied) return; const t = setTimeout(() => setCopied(false), 2500); return () => clearTimeout(t); }, [copied]);

  // Poll the persisted job while it runs; the card re-reads title / description / x_post from the same response (single source).
  useEffect(() => {
    if (job?.status !== "processing" || !m?.id) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/owner/media/${m.id}/reprocess-status`);
        setJob(data.reprocess); setCloud(data.media_store || data.cloudinary);
        if (data.media) setM((prev) => ({ ...prev, ...data.media }));
        if (data.reprocess?.status !== "processing") { onChanged?.(); return; }
      } catch (e) { /* transient — keep polling */ }
      timer.current = setTimeout(tick, POLL_MS);
    };
    timer.current = setTimeout(tick, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [job?.status, m?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!m) return null;
  const xText = (m.x_post || "").trim();           // the ONE string that is displayed AND copied
  const xBad = xPostProblems(xText);
  const chip = jobChip(job);
  const loc = ownerLocationLabel(m);
  const isVideo = m.media_type === "video";
  const cloudDown = cloud?.available === false || job?.video?.status === "blocked";
  const playable = isVideo && !!videoUrl(m) && !cloudDown;
  const title = displayTitle(m.title) || m.title || "Untitled";

  const copyForX = async () => {
    if (xBad.length) { toast.error(`X text not ready (${xBad.join(", ")})`); return; }
    if (await copyText(xText)) { setCopied(true); toast.success("Copied for X"); }
    else toast.error("Could not copy — long-press the text to copy manually");
  };

  const reprocess = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/owner/media/${m.id}/reprocess`, { force: true });
      setJob(data.reprocess);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) { toast.message("Already processing"); setJob(d?.reprocess || { status: "processing", stage: "running" }); }
      else toast.error(`Could not start: ${d?.message || d || e.message}`);
    } finally { setStarting(false); }
  };

  return (
    <article data-testid={`owner-card-${m.id}`} className="rounded-2xl border border-primary/40 bg-card/80 overflow-hidden shadow-lg shadow-primary/10">
      <div className="bg-black aspect-[4/5] sm:aspect-video max-h-[60vh] flex items-center justify-center relative" data-testid={`owner-card-media-${m.id}`}>
        {isVideo ? (
          playable ? (
            <video src={videoUrl(m)} poster={posterUrl(m)} controls playsInline controlsList="nodownload" className="w-full h-full object-contain" />
          ) : (
            <div className="text-center px-6 space-y-2" data-testid={`owner-card-video-blocked-${m.id}`}>
              {posterUrl(m) && !cloudDown ? <img src={posterUrl(m)} alt="" className="max-h-[40vh] mx-auto rounded object-contain opacity-60" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Video className="w-10 h-10 mx-auto text-muted-foreground" />}
              {cloudDown ? (
                <>
                  <div className="text-sky-300 text-sm inline-flex items-center gap-1.5"><CloudOff className="w-4 h-4" /> Video playback unavailable</div>
                  <div className="text-xs text-muted-foreground">{["r2","bf"].includes(m.video_backend) ? "Media storage is currently unavailable, so playback is on hold. The original is safe." : "Media storage is currently unavailable, so playback is on hold. The original is safe."}</div>
                </>
              ) : job?.video?.status === "failed" || m.video_failed ? (
                <div className="text-amber-300 text-sm inline-flex items-center gap-1.5"><CloudOff className="w-4 h-4" /> Video processing failed — run Reprocess</div>
              ) : (
                <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Processing video…</div>
              )}
            </div>
          )
        ) : (
          <img src={thumbUrl(m.file_ref, 1200)} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
      </div>

      <div className="p-4 space-y-4">
        <div>
          <h2 className="text-xl font-heading font-semibold leading-tight" data-testid={`owner-card-title-${m.id}`}>{title}</h2>
          <div className={cn("text-[11px] mt-1 flex items-start gap-1", loc.key === "verify" || loc.key === "pending" ? "text-amber-200" : "text-muted-foreground")} data-testid={`owner-card-location-${m.id}`}>
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span>{loc.label}{loc.key === "exact" ? " · Exact" : loc.key === "general" ? " · General" : loc.key === "verify" ? " · Needs verification" : ""}</span>
          </div>
        </div>

        <section>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">Description</h3>
          <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`owner-card-description-${m.id}`}>{(m.description || "").trim() || <span className="text-muted-foreground">No description yet.</span>}</p>
        </section>

        <section className="rounded-xl border border-border bg-background/60 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">X Post</h3>
            <span className={cn("text-[11px]", xText.length > 280 ? "text-red-300" : "text-muted-foreground")} data-testid={`owner-card-xpost-count-${m.id}`}>{xText.length}/280</span>
          </div>
          <p className="text-sm leading-relaxed whitespace-pre-wrap select-text" data-testid={`owner-card-xpost-${m.id}`}>{xText || <span className="text-muted-foreground">No X text yet — run Reprocess.</span>}</p>
          {xBad.length > 0 && xText && <div className="text-xs text-red-300 inline-flex items-center gap-1" data-testid={`owner-card-xpost-problem-${m.id}`}><AlertTriangle className="w-3 h-3" /> Not copy-safe: {xBad.join(", ")}</div>}
          <Button type="button" data-testid={`owner-card-copy-x-${m.id}`} onClick={copyForX} disabled={!xText || xBad.length > 0}
            className={cn("w-full h-12 rounded-full font-semibold text-base", copied && "bg-emerald-600 hover:bg-emerald-600 text-white")}>
            {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied for X</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy for X</>}
          </Button>
        </section>

        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" data-testid={`owner-card-reprocess-${m.id}`} onClick={reprocess} disabled={starting || job?.status === "processing"} className="h-11 rounded-full font-semibold flex-1">
            {job?.status === "processing" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Processing…</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Reprocess</>}
          </Button>
          <button type="button" data-testid={`owner-card-edit-${m.id}`} onClick={onEdit} aria-label="Edit"
            className="h-11 w-11 rounded-full border border-border bg-secondary/40 flex items-center justify-center text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
        </div>
        {chip && (
          <div className={cn("text-xs flex items-start gap-1.5", CHIP_CLASS[chip.key])} data-testid={`owner-card-job-${m.id}`} data-state={chip.key}>
            {chip.key === "processing" ? <Loader2 className="w-3 h-3 mt-0.5 animate-spin shrink-0" /> : chip.key === "succeeded" ? <Check className="w-3 h-3 mt-0.5 shrink-0" /> : chip.key === "blocked" ? <CloudOff className="w-3 h-3 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />}
            <span>
              {chip.label}
              {chip.key === "blocked" && <span className="block text-muted-foreground">{job.video?.detail || cloud?.detail || (["r2","bf"].includes(m.video_backend) ? "Video publish is on hold until media storage is reachable." : "Video publish is on hold until media storage is reachable.")} Caption: {job.caption?.status || "—"}.</span>}
              {chip.key === "succeeded" && job.caption?.protected?.length > 0 && <span className="block text-muted-foreground">Kept your edited {job.caption.protected.join(", ")}.</span>}
            </span>
          </div>
        )}

        <OwnerChat media={m} onWritten={(fresh) => { setM((prev) => ({ ...prev, ...fresh })); onChanged?.(); }} />
      </div>
    </article>
  );
};
